import styles from "./ReasonToBuy.module.scss";
import { ReactNode, useRef, useState, useEffect } from "react";

type Card = {
  id: string;
  title: string;
  description?: string;
  icon?: ReactNode;
};

type Props = {
  cards: Card[];
};

export const ReasonToBuyUI = ({ cards }: Props) => {
  const trackRef = useRef<HTMLUListElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    const handleScroll = () => {
      const cardWidth = track.scrollWidth / cards.length;
      const index = Math.round(track.scrollLeft / cardWidth);
      setActiveIndex(Math.min(index, cards.length - 1));
    };

    track.addEventListener("scroll", handleScroll, { passive: true });
    return () => track.removeEventListener("scroll", handleScroll);
  }, [cards.length]);

  if (!cards.length) {
    return null;
  }

  return (
    <div data-component="ReasonToBuy" className={styles.container}>
      <ul
        className={styles.cards}
        data-testid="reason-to-buy-cards-track"
        ref={trackRef}
      >
        {cards.map((card) => (
          <li
            key={card.id}
            className={styles.card}
            data-testid={`reason-to-buy-card-${card.id}`}
          >
            <div className={styles.cardHeader}>
              {card.icon && (
                <span className={styles.cardIcon}>{card.icon}</span>
              )}
            </div>
            <p className={styles.cardTitle}>{card.title}</p>
            {card.description && (
              <p className={styles.cardDescription}>{card.description}</p>
            )}
          </li>
        ))}
      </ul>
      {cards.length > 1 && (
        <div
          className={styles.pagination}
          aria-hidden="true"
          data-testid="reason-to-buy-pagination"
        >
          {cards.map((card, index) => (
            <span
              key={`pagination-dot-${card.id}-${index}`}
              className={styles.paginationDot}
              data-active={index === activeIndex}
            />
          ))}
        </div>
      )}
    </div>
  );
};
